import React, { useState, useEffect, useLayoutEffect } from "react";
import { useParams, Link } from 'react-router-dom';
import { Minus, Plus, ShoppingCart } from 'lucide-react';
import Layout from '../components/layout/Layout';
import * as Types from '../services/api/api.types';
import { useCart } from '../contexts/CartContext';
import { config } from '../config/env';
import { useNotification } from '../contexts/NotificationContext';
import { apiService } from '../services/api/instance';
import logger from '../utils/logger';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const ProductPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [product, setProduct] = useState<Types.ProductFull | null>(null);
    // Linked only for Vali: the other feeds name a category we have no page for.
    const [categoryCrumb, setCategoryCrumb] = useState<{ name: string; path: string | null } | null>(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [activeImage, setActiveImage] = useState(0);
    const { cart, addToCart } = useCart();
    const { showNotification } = useNotification();

    useLayoutEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [slug]);

    useEffect(() => {
        const fetchProductBySlug = async () => {
            if (slug) {
                try {
                    setLoading(true);
                    setCategoryCrumb(null);

                    const resolved = await apiService.resolveProductSlug(slug);

                    if (!resolved) {
                        setProduct(null);
                        setLoading(false);
                        return;
                    }

                    const data = await apiService.getProductBySupplierFull(resolved.supplier, resolved.productId);
                    const categories = (data as any)?.categories;
                    if (resolved.supplier === 'vali') {
                        const category = (await apiService.getCategories()).find(cat => cat.id === categories?.[0]?.id);
                        const name = category?.name.find(n => n.language_code === 'bg')?.text;
                        if (category && name) {
                            setCategoryCrumb({ name, path: `/category/${category.slug ?? category.id}` });
                        }
                    } else if (Array.isArray(categories) && categories.length > 0) {
                        const name = typeof categories[0] === 'string' ? categories[0] : categories[0]?.name;
                        if (name) setCategoryCrumb({ name, path: null });
                    }

                    if (!data) {
                        setProduct(null);
                        setLoading(false);
                        return;
                    }

                    setProduct({ ...data, supplier: resolved.supplier });

                    if (data.images && data.images.length > 0) {
                        setActiveImage(0);
                    }
                } catch (error) {
                    logger.error('Грешка при зареждането на продукта:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchProductBySlug();
    }, [slug]);

    const formatEUR = (amount: number): string => {
        return `€${amount.toFixed(2)}`;
    };

    const isProductAvailable = (): boolean => {
        return product?.status === 1;
    };

    const getBgName = (names?: Types.Name[]): string => {
        if (!names) return '';
        const bgName = names.find(name => name.language_code === 'bg');
        return bgName ? bgName.text : '';
    };

    useDocumentTitle(product ? getBgName(product.name) : null);

    const getBgDescription = (descriptions?: Types.Description[]): string => {
        if (!descriptions) return '';
        const bgDescription = descriptions.find(desc => desc.language_code === 'bg');
        return bgDescription ? bgDescription.text : '';
    };

    /**
     * The two spec shapes the suppliers arrive in, reduced to one table.
     *
     * Vali serves one row per (parameter, option) pair straight off its own
     * API, repeating the parameter once per option; Asbis and Dekada come
     * through our backend as flat `{name, value}`, which repeats the name the
     * same way. Both are pairs, so both group by name.
     */
    const getSpecs = (): Array<{ name: string; values: string[] }> => {
        if (!product) return [];

        const pairs: Array<[string, string]> = product.parameters && product.parameters.length > 0
            ? product.parameters.map(row => [getBgName(row.parameter_name), getBgName(row.option_name)])
            : (product.attributes ?? []).map(attribute => [attribute.name, attribute.value]);

        const grouped = new Map<string, string[]>();
        for (const [name, value] of pairs) {
            if (!name || !value) continue;
            const values = grouped.get(name) ?? [];
            if (!values.includes(value)) values.push(value);
            grouped.set(name, values);
        }
        return Array.from(grouped, ([name, values]) => ({ name, values }));
    };

    const handleAddToCart = () => {
        if (product && isProductAvailable()) {
            addToCart({
                id: product.id,
                supplier: product.supplier ?? 'vali',
                slug: product.slug ?? null,
                referenceNumber: product.reference_number,
                name: getBgName(product.name),
                price: Number(product.price_partner),
                pricePromo: product.price_promo ? Number(product.price_promo) : null,
                quantity: quantity,
                image: product.images && product.images.length > 0 ? product.images[0].href : null
            });

            if (showNotification) {
                showNotification('success', `${getBgName(product.name)} е добавен в количката`, 3000);
            }
        }
    };

    const decreaseQuantity = () => {
        if (quantity > 1) {
            setQuantity(quantity - 1);
        }
    };

    const increaseQuantity = () => {
        setQuantity(quantity + 1);
    };

    const PriceDisplay: React.FC<{ price: number; isOldPrice?: boolean; isPromoPrice?: boolean }> = ({ price, isOldPrice = false, isPromoPrice = false }) => {
        const textClass = isOldPrice ? 'text-gray-500' : (isPromoPrice ? 'text-green-600' : 'text-gray-900');
        const strikeClass = isOldPrice ? 'line-through' : '';

        return (
            <div className={`${textClass} ${strikeClass}`}>
                <div className="text-2xl font-bold">
                    {formatEUR(price)}
                </div>
                {!isOldPrice && (
                    <div className="text-sm text-gray-500 mt-1">
                        Цена с ДДС
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
                <div className="flex justify-center items-center min-h-screen">
                    <p className="text-lg">Зареждане...</p>
                </div>
            </Layout>
        );
    }

    if (!product) {
        return (
            <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
                <div className="text-center py-8">
                    <h2 className="text-2xl font-bold mb-4">Продуктът не е намерен</h2>
                    <Link to="/" className="text-blue-600 hover:underline">
                        Върнете се към началната страница
                    </Link>
                </div>
            </Layout>
        );
    }

    const specs = getSpecs();

    return (
        <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
            <div className="container mx-auto py-6">
                <div className="text-sm mb-6 text-gray-600">
                    <Link to="/" className="hover:text-primary-600">Начало</Link>
                    {' > '}
                    {categoryCrumb && (
                        <>
                            {categoryCrumb.path ? (
                                <Link to={categoryCrumb.path} className="hover:text-primary-600">
                                    {categoryCrumb.name}
                                </Link>
                            ) : (
                                <span>{categoryCrumb.name}</span>
                            )}
                            {' > '}
                        </>
                    )}
                    <span className="font-medium">{getBgName(product.name)}</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    <div>
                        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white p-4 mb-4">
                            {product.images && product.images.length > 0 ? (
                                <img
                                    src={product.images[activeImage].href}
                                    fetchpriority="high"
                                    alt={getBgName(product.name)}
                                    className="w-full h-64 sm:h-80 md:h-96 object-contain"
                                />
                            ) : (
                                <div className="w-full h-64 sm:h-80 md:h-96 flex items-center justify-center bg-gray-100">
                                    <span className="text-gray-400">Няма изображение</span>
                                </div>
                            )}
                        </div>

                        {product.images && product.images.length > 1 && (
                            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                                {product.images.map((image, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setActiveImage(index)}
                                        className={`border rounded p-1 h-12 sm:h-16 ${index === activeImage ? 'border-primary-500' : 'border-gray-200'
                                            }`}
                                    >
                                        <img
                                            src={image.href}
                                            loading="lazy"
                                            fetchpriority="low"
                                            alt={`${getBgName(product.name)} - изображение ${index + 1}`}
                                            className="w-full h-full object-contain"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold mb-2">{getBgName(product.name)}</h1>

                        <div className="mb-4 text-sm">
                            <p>Производител: <span className="font-medium">{product.manufacturer}</span></p>
                            <p>Модел: <span className="font-medium">{product.model}</span></p>
                            <p>Референтен номер: <span className="font-medium">{product.reference_number}</span></p>
                            {product.warranty > 0 && (
                                <p>Гаранция: <span className="font-medium">{product.warranty} месеца</span></p>
                            )}
                        </div>

                        <div className="mb-6 flex items-center gap-4">
                            {product.price_promo !== null && product.price_promo > 1 ? (
                                <>
                                    <PriceDisplay price={Number(product.price_promo)} isPromoPrice={true} />
                                    <PriceDisplay price={Number(product.price_partner)} isOldPrice={true} />
                                </>
                            ) : (
                                <PriceDisplay price={Number(product.price_partner)} />
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
                            <div className="flex items-center border border-gray-300 rounded self-start sm:self-auto">
                                <button
                                    onClick={decreaseQuantity}
                                    className="px-3 py-2 text-gray-600 hover:bg-gray-100"
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <span className="px-4 py-2 border-l border-r border-gray-300 min-w-[60px] text-center">{quantity}</span>
                                <button
                                    onClick={increaseQuantity}
                                    className="px-3 py-2 text-gray-600 hover:bg-gray-100"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>

                            <button
                                onClick={handleAddToCart}
                                disabled={!isProductAvailable()}
                                className={`flex-1 py-3 px-4 rounded flex items-center justify-center transition-colors ${isProductAvailable()
                                    ? 'bg-primary-500 hover:bg-primary-600 text-white'
                                    : 'bg-gray-300 text-gray-700 cursor-not-allowed'
                                    }`}
                            >
                                <ShoppingCart className="mr-2 h-5 w-5" />
                                {isProductAvailable() ? 'Добави в количката' : 'Няма наличност'}
                            </button>
                        </div>

                        {/* Desc */}
                        <div className="border-t border-gray-200 pt-6">
                            <h2 className="text-xl font-bold mb-3">Описание</h2>
                            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: getBgDescription(product.description) }}></div>
                        </div>
                    </div>
                </div>

                {/* Params */}
                {specs.length > 0 && (
                    <div className="mt-8 max-w-3xl">
                        <h2 className="text-lg font-bold mb-3">Технически характеристики</h2>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <tbody>
                                    {specs.map((spec, index) => (
                                        <tr key={spec.name} className={index > 0 ? 'border-t border-gray-200' : ''}>
                                            <td className="px-3 py-1.5 text-gray-600 w-1/3 bg-gray-50 align-top">
                                                {spec.name}
                                            </td>
                                            <td className="px-3 py-1.5">
                                                {spec.values.map(value => (
                                                    <div key={value}>{value}</div>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Docs */}
                {product.documents && product.documents.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-xl font-bold mb-6">Документи</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {product.documents.map((doc, index) => (
                                <a
                                    key={index}
                                    href={doc.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center p-3 border border-gray-200 rounded hover:bg-gray-50"
                                >
                                    <div className="mr-3 p-2 bg-gray-100 rounded">
                                        <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <span>{getBgName(doc.comment)}</span>
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ProductPage;